import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Star, StarOff, Calendar, X, Save, Users, CheckCircle2, Circle, Mail, Phone } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";
import { EVENT_TYPES, EVENT_TYPE_META, formatDateTime } from "../../lib/events";

const EMPTY_BI = { tr: "", en: "" };

const blankEvent = () => ({
  title: { ...EMPTY_BI },
  description: { ...EMPTY_BI },
  body: { ...EMPTY_BI },
  image: "",
  type: "zoom",
  date: toLocalInput(new Date(Date.now() + 7 * 24 * 3600 * 1000)),
  end_date: "",
  location: { ...EMPTY_BI },
  location_url: "",
  price: { ...EMPTY_BI },
  capacity: "",
  registration_url: "",
  featured: false,
  status: "published",
});

function toLocalInput(d) {
  // Convert to "YYYY-MM-DDTHH:mm" in local time for <input type="datetime-local">
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function AdminEvents() {
  const { authedClient } = useAdminAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?:..., ...form}
  const [regsFor, setRegsFor] = useState(null); // event being inspected
  const [regCounts, setRegCounts] = useState({}); // {event_id: count}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ev }, { data: regs }] = await Promise.all([
        authedClient.get("/admin/events"),
        authedClient.get("/admin/registrations"),
      ]);
      setEvents(ev || []);
      const counts = {};
      (regs || []).forEach((r) => {
        counts[r.event_id] = (counts[r.event_id] || 0) + 1;
      });
      setRegCounts(counts);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => setEditing({ ...blankEvent(), _new: true });
  const startEdit = (ev) =>
    setEditing({
      ...blankEvent(),
      ...ev,
      date: toLocalInput(ev.date),
      end_date: toLocalInput(ev.end_date),
      capacity: ev.capacity ?? "",
      _id: ev.id,
    });

  const toggleFeatured = async (ev) => {
    try {
      await authedClient.put(`/admin/events/${ev.id}`, {
        ...ev,
        featured: !ev.featured,
        date: ev.date,
        end_date: ev.end_date || null,
      });
      load();
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`"${ev.title?.tr || ev.title?.en}" etkinliğini silmek istediğinize emin misiniz?`)) return;
    try {
      await authedClient.delete(`/admin/events/${ev.id}`);
      toast.success("Etkinlik silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const save = async (form) => {
    const payload = {
      title: form.title,
      description: form.description,
      body: form.body,
      image: form.image || null,
      type: form.type,
      date: fromLocalInput(form.date),
      end_date: fromLocalInput(form.end_date),
      location: form.location,
      location_url: form.location_url || null,
      price: form.price,
      capacity: form.capacity === "" || form.capacity == null ? null : Number(form.capacity),
      registration_url: form.registration_url || null,
      featured: !!form.featured,
      status: form.status,
    };
    if (!payload.date) {
      toast.error("Lütfen geçerli bir tarih girin");
      return;
    }
    if (!payload.title.tr && !payload.title.en) {
      toast.error("En az bir dilde başlık girin (TR veya EN)");
      return;
    }
    try {
      if (form._new) {
        await authedClient.post("/admin/events", payload);
        toast.success("Etkinlik oluşturuldu");
      } else {
        await authedClient.put(`/admin/events/${form._id}`, payload);
        toast.success("Etkinlik güncellendi");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  return (
    <div data-testid="admin-events-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Etkinlikler</h1>
          <p className="mt-1 text-sm text-ink/60">
            Toplam <strong>{events.length}</strong> · Öne çıkan: <strong>{events.filter((e) => e.featured).length}</strong>
          </p>
        </div>
        <button onClick={startCreate} data-testid="event-create" className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          Yeni Etkinlik
        </button>
      </header>

      {loading ? (
        <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>
      ) : events.length === 0 ? (
        <div className="surface-card p-10 text-center text-ink/60">
          <Calendar className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
          Henüz etkinlik yok. <strong>Yeni Etkinlik</strong> ile ilkini oluşturun.
        </div>
      ) : (
        <ul className="space-y-3">
          {events.map((ev) => {
            const Icon = (EVENT_TYPE_META[ev.type] || EVENT_TYPE_META.other).icon;
            const tone = (EVENT_TYPE_META[ev.type] || EVENT_TYPE_META.other).tone;
            return (
              <li
                key={ev.id}
                data-testid={`event-row-${ev.id}`}
                className="surface-card p-5 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-cream-200 shrink-0">
                    {ev.image ? (
                      <img src={ev.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-cream-300">
                        <Icon className="w-6 h-6" strokeWidth={1.2} />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 rounded-full ${tone}`}>
                        <Icon className="w-3 h-3" strokeWidth={1.6} />
                        {EVENT_TYPE_META[ev.type]?.label?.tr || ev.type}
                      </span>
                      {ev.status === "draft" && (
                        <span className="text-[10px] uppercase tracking-[0.18em] bg-cream-300 text-ink/60 px-2 py-0.5 rounded-full">Taslak</span>
                      )}
                    </div>
                    <p className="mt-1.5 font-serif text-xl text-ink leading-tight truncate">{ev.title?.tr || ev.title?.en}</p>
                    <p className="text-xs text-ink/55 mt-0.5 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" strokeWidth={1.5} />
                      {formatDateTime(ev.date, "tr")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:flex-col md:items-stretch md:gap-1">
                  <button
                    onClick={() => setRegsFor(ev)}
                    data-testid={`event-regs-${ev.id}`}
                    title="Kayıtları gör"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-sage-300/60 text-sage-700 hover:bg-sage-100"
                  >
                    <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Kayıtlar ({regCounts[ev.id] || 0})
                  </button>
                  <button
                    onClick={() => toggleFeatured(ev)}
                    data-testid={`event-feature-${ev.id}`}
                    title={ev.featured ? "Öne çıkmayı kaldır" : "Öne çıkar"}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${
                      ev.featured ? "bg-terracotta-400 text-cream-100" : "border border-ink/15 text-ink/65 hover:text-ink"
                    }`}
                  >
                    {ev.featured ? <Star className="w-3.5 h-3.5 fill-cream-100" strokeWidth={1.5} /> : <StarOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    Öne çıkar
                  </button>
                  <button
                    onClick={() => startEdit(ev)}
                    data-testid={`event-edit-${ev.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-ink/15 text-ink/65 hover:text-ink"
                  >
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Düzenle
                  </button>
                  <button
                    onClick={() => remove(ev)}
                    data-testid={`event-delete-${ev.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-terracotta-200 text-terracotta-500 hover:bg-terracotta-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Sil
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <EventEditor form={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={save} />
      )}

      {regsFor && (
        <RegistrationsModal
          ev={regsFor}
          onClose={() => {
            setRegsFor(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function RegistrationsModal({ ev, onClose }) {
  const { authedClient } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("registered");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [regs, waits] = await Promise.all([
        authedClient.get(`/admin/events/${ev.id}/registrations`),
        authedClient.get(`/admin/events/${ev.id}/waitlist`),
      ]);
      setItems(regs.data || []);
      setWaitlist(waits.data || []);
    } catch (e) {
      toast.error("Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient, ev.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAttended = async (r) => {
    try {
      await authedClient.patch(`/admin/registrations/${r.id}`, null, { params: { attended: !r.attended } });
      setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, attended: !r.attended } : x)));
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.name} adlı kişinin kaydını silmek istiyor musunuz?`)) return;
    try {
      await authedClient.delete(`/admin/registrations/${r.id}`);
      setItems((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Kayıt silindi");
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const promoteWaitlist = async (w) => {
    if (!window.confirm(`${w.name} adlı kişiyi bekleme listesinden onaylı kayıtlara taşımak istiyor musunuz? Onay e-postası ve takvim eki gönderilecek.`)) return;
    try {
      await authedClient.post(`/admin/waitlist/${w.id}/promote`);
      toast.success("Kayda dönüştürüldü, onay e-postası gönderildi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const deleteWaitlist = async (w) => {
    if (!window.confirm(`${w.name} kaydını bekleme listesinden silmek istiyor musunuz?`)) return;
    try {
      await authedClient.delete(`/admin/waitlist/${w.id}`);
      setWaitlist((prev) => prev.filter((x) => x.id !== w.id));
      toast.success("Bekleme listesinden silindi");
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const exportCsv = () => {
    const source = tab === "registered" ? items : waitlist;
    const rows = [
      ["Name", "Email", "Phone", "Guests", tab === "registered" ? "Attended" : "Promoted", "Created At", "Notes"],
      ...source.map((r) => [
        r.name, r.email, r.phone || "", r.guests,
        tab === "registered" ? (r.attended ? "yes" : "no") : (r.promoted ? "yes" : "no"),
        r.created_at, (r.notes || "").replace(/[\r\n]+/g, " "),
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `intouch-${tab}-${ev.id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totalGuests = items.reduce((s, r) => s + (r.guests || 1), 0);
  const list = tab === "registered" ? items : waitlist;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div
        data-testid="registrations-modal"
        className="bg-cream-100 rounded-3xl shadow-float w-full max-w-4xl max-h-[92vh] overflow-y-auto animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-cream-100 border-b border-cream-300/60 px-7 py-5 z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-terracotta-400 font-medium">Kayıtlar</p>
              <h2 className="font-serif text-2xl text-ink mt-1">{ev.title?.tr || ev.title?.en}</h2>
              <p className="text-xs text-ink/55 mt-1">
                {items.length} onaylı kayıt · {totalGuests} kişi
                {ev.capacity ? ` / ${ev.capacity}` : ""}
                {waitlist.length > 0 && ` · ${waitlist.length} bekleme listesinde`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportCsv} disabled={!list.length} data-testid="reg-export-csv" className="text-xs px-4 py-2 rounded-full border border-ink/15 hover:bg-cream-200 disabled:opacity-40">CSV indir</button>
              <button onClick={onClose} className="text-ink/40 hover:text-ink" aria-label="close"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
          </div>
          <div className="flex items-center gap-1 mt-4">
            <button data-testid="reg-tab-registered" onClick={() => setTab("registered")} className={`text-xs uppercase tracking-[0.22em] px-3 py-1.5 rounded-full transition-colors ${tab === "registered" ? "bg-ink text-cream-100" : "text-ink/55 hover:text-ink"}`}>
              Onaylı ({items.length})
            </button>
            <button data-testid="reg-tab-waitlist" onClick={() => setTab("waitlist")} className={`text-xs uppercase tracking-[0.22em] px-3 py-1.5 rounded-full transition-colors ${tab === "waitlist" ? "bg-ink text-cream-100" : "text-ink/55 hover:text-ink"}`}>
              Bekleme listesi ({waitlist.length})
            </button>
          </div>
        </div>
        <div className="p-7">
          {loading ? (
            <div className="text-ink/55 text-sm py-10 text-center">Yükleniyor…</div>
          ) : list.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
              <p className="text-ink/60">{tab === "registered" ? "Henüz onaylı kayıt yok." : "Bekleme listesi boş."}</p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {list.map((r) => tab === "registered" ? (
                <li key={r.id} data-testid={`reg-row-${r.id}`} className="bg-white border border-cream-300/60 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <button onClick={() => toggleAttended(r)} data-testid={`reg-attended-${r.id}`} title={r.attended ? "Katıldı olarak işaretli" : "Katılmadı olarak işaretli"} className="shrink-0">
                    {r.attended ? <CheckCircle2 className="w-5 h-5 text-sage-500" strokeWidth={1.5} /> : <Circle className="w-5 h-5 text-ink/30" strokeWidth={1.5} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink truncate">{r.name}{r.guests > 1 && <span className="text-ink/45 text-sm font-light"> · {r.guests} kişi</span>}</p>
                    <p className="text-sm text-ink/55 flex flex-wrap gap-x-3 gap-y-0.5">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-terracotta-400"><Mail className="w-3 h-3" strokeWidth={1.5} />{r.email}</a>
                      {r.phone && <a href={`tel:${r.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 hover:text-terracotta-400"><Phone className="w-3 h-3" strokeWidth={1.5} />{r.phone}</a>}
                    </p>
                    {r.notes && <p className="text-sm text-ink/65 italic mt-1.5 font-light leading-relaxed">"{r.notes}"</p>}
                  </div>
                  <div className="text-xs text-ink/45 shrink-0">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
                  <button onClick={() => remove(r)} data-testid={`reg-delete-${r.id}`} title="Sil" className="shrink-0 text-ink/40 hover:text-terracotta-500 p-1">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </li>
              ) : (
                <li key={r.id} data-testid={`waitlist-row-${r.id}`} className="bg-white border border-cream-300/60 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <Users className="w-5 h-5 text-bluefog-400 shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink truncate">
                      {r.name}
                      {r.guests > 1 && <span className="text-ink/45 text-sm font-light"> · {r.guests} kişi</span>}
                      {r.promoted && <span className="ml-2 text-[10px] uppercase tracking-widest text-sage-700 bg-sage-100 px-2 py-0.5 rounded-full">Onaylandı</span>}
                    </p>
                    <p className="text-sm text-ink/55 flex flex-wrap gap-x-3 gap-y-0.5">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-terracotta-400"><Mail className="w-3 h-3" strokeWidth={1.5} />{r.email}</a>
                      {r.phone && <a href={`tel:${r.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 hover:text-terracotta-400"><Phone className="w-3 h-3" strokeWidth={1.5} />{r.phone}</a>}
                    </p>
                    {r.notes && <p className="text-sm text-ink/65 italic mt-1.5 font-light leading-relaxed">"{r.notes}"</p>}
                  </div>
                  <div className="text-xs text-ink/45 shrink-0">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!r.promoted && (
                      <button onClick={() => promoteWaitlist(r)} data-testid={`waitlist-promote-${r.id}`} title="Onaylı kayıtlara dönüştür" className="text-xs px-3 py-1.5 rounded-full bg-sage-100 text-sage-700 hover:bg-sage-200">
                        Onayla
                      </button>
                    )}
                    <button onClick={() => deleteWaitlist(r)} data-testid={`waitlist-delete-${r.id}`} title="Sil" className="text-ink/40 hover:text-terracotta-500 p-1">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function EventEditor({ form, onChange, onCancel, onSave }) {
  const setBi = (field) => (lang, v) =>
    onChange((p) => ({ ...p, [field]: { ...p[field], [lang]: v } }));

  const setVal = (field) => (v) => onChange((p) => ({ ...p, [field]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div
        data-testid="event-editor"
        className="bg-cream-100 rounded-3xl shadow-float w-full max-w-3xl max-h-[92vh] overflow-y-auto animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-cream-100 border-b border-cream-300/60 px-7 py-5 flex items-center justify-between z-10">
          <h2 className="font-serif text-2xl text-ink">{form._new ? "Yeni Etkinlik" : "Etkinliği Düzenle"}</h2>
          <button onClick={onCancel} className="text-ink/40 hover:text-ink" aria-label="close"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>
        <div className="p-7 space-y-5">
          <Bi label="Başlık" value={form.title} onChange={setBi("title")} testid="ev-title" required />
          <Bi label="Kısa açıklama" multiline rows={2} value={form.description} onChange={setBi("description")} testid="ev-desc" />
          <Bi label="Detay metni (modal için)" multiline rows={5} value={form.body} onChange={setBi("body")} testid="ev-body" />

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <span className="admin-label">Tip</span>
              <select
                data-testid="ev-type"
                value={form.type}
                onChange={(e) => setVal("type")(e.target.value)}
                className="admin-input"
              >
                {EVENT_TYPES.map((tp) => (
                  <option key={tp} value={tp}>{EVENT_TYPE_META[tp]?.label?.tr || tp}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="admin-label">Durum</span>
              <select
                data-testid="ev-status"
                value={form.status}
                onChange={(e) => setVal("status")(e.target.value)}
                className="admin-input"
              >
                <option value="published">Yayında</option>
                <option value="draft">Taslak</option>
              </select>
            </div>
            <div>
              <span className="admin-label">Başlangıç tarihi</span>
              <input
                type="datetime-local"
                data-testid="ev-date"
                value={form.date}
                onChange={(e) => setVal("date")(e.target.value)}
                className="admin-input"
                required
              />
            </div>
            <div>
              <span className="admin-label">Bitiş tarihi (opsiyonel)</span>
              <input
                type="datetime-local"
                data-testid="ev-end-date"
                value={form.end_date || ""}
                onChange={(e) => setVal("end_date")(e.target.value)}
                className="admin-input"
              />
            </div>
          </div>

          <div>
            <span className="admin-label">Görsel URL</span>
            <input
              data-testid="ev-image"
              type="url"
              value={form.image || ""}
              onChange={(e) => setVal("image")(e.target.value)}
              placeholder="https://intouch.consulting/img/event.jpg"
              className="admin-input"
            />
            {form.image && (
              <div className="mt-3 w-48 aspect-[16/10] rounded-lg overflow-hidden border border-cream-300/60">
                <img src={form.image} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <Bi label="Konum (örn. 'Zoom', 'İstanbul - Kadıköy')" value={form.location} onChange={setBi("location")} testid="ev-location" />
          <div>
            <span className="admin-label">Konum / Zoom bağlantısı (opsiyonel)</span>
            <input
              data-testid="ev-location-url"
              type="url"
              value={form.location_url || ""}
              onChange={(e) => setVal("location_url")(e.target.value)}
              placeholder="https://zoom.us/j/…  veya  https://maps.app.goo.gl/…"
              className="admin-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Bi label="Ücret (örn. 'Ücretsiz' / 'Free' / '250 TL')" value={form.price} onChange={setBi("price")} testid="ev-price" />
            <div>
              <span className="admin-label">Kapasite (opsiyonel)</span>
              <input
                data-testid="ev-capacity"
                type="number"
                min="1"
                value={form.capacity ?? ""}
                onChange={(e) => setVal("capacity")(e.target.value)}
                className="admin-input"
              />
            </div>
          </div>

          <div>
            <span className="admin-label">Kayıt linki (opsiyonel)</span>
            <input
              data-testid="ev-reg-url"
              type="url"
              value={form.registration_url || ""}
              onChange={(e) => setVal("registration_url")(e.target.value)}
              placeholder="https://forms.google.com/…  veya  mailto:hello@intouch.consulting"
              className="admin-input"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.featured}
              onChange={(e) => setVal("featured")(e.target.checked)}
              data-testid="ev-featured"
              className="w-4 h-4 accent-terracotta-400"
            />
            Üst slider'da öne çıkar
          </label>
        </div>
        <div className="sticky bottom-0 bg-cream-100 border-t border-cream-300/60 px-7 py-4 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-secondary">İptal</button>
          <button onClick={() => onSave(form)} data-testid="ev-save" className="btn-primary">
            <Save className="w-4 h-4" strokeWidth={1.5} />
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

function Bi({ label, value, onChange, multiline = false, rows = 3, testid = "", required = false }) {
  return (
    <div>
      <span className="admin-label">{label}{required && <span className="text-terracotta-400 ml-1">*</span>}</span>
      <div className="grid md:grid-cols-2 gap-3">
        {["tr", "en"].map((lang) => (
          <div key={lang}>
            <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{lang.toUpperCase()}</span>
            {multiline ? (
              <textarea
                data-testid={`${testid}-${lang}`}
                rows={rows}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-textarea"
              />
            ) : (
              <input
                data-testid={`${testid}-${lang}`}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-input"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
